import React from 'react';

/**
 * Empty State Component
 * Provides consistent empty state messaging across the application
 */

const EmptyState = ({ 
  icon = '📭',
  title = 'No items found',
  description = 'There are no items to display at the moment.',
  actionText,
  onAction,
  variant = 'default'
}) => {
  const variants = {
    default: {
      iconColor: '#9ca3af',
      titleColor: '#d1d5db',
      descriptionColor: '#6b7280'
    },
    success: {
      iconColor: '#10b981',
      titleColor: '#34d399',
      descriptionColor: '#6ee7b7'
    },
    warning: {
      iconColor: '#f59e0b',
      titleColor: '#fbbf24',
      descriptionColor: '#fcd34d'
    },
    error: {
      iconColor: '#ef4444',
      titleColor: '#f87171',
      descriptionColor: '#fca5a5'
    }
  };

  const currentVariant = variants[variant];

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 16px',
      minHeight: '300px',
      textAlign: 'center'
    }}>
      <div style={{ maxWidth: '400px' }}>
        <div style={{
          fontSize: '3rem',
          marginBottom: '16px',
          color: currentVariant.iconColor
        }}>
          {icon}
        </div>
        
        <h3 style={{
          fontSize: '1.5rem',
          fontWeight: '600',
          color: currentVariant.titleColor,
          marginBottom: '8px'
        }}>
          {title}
        </h3>
        
        <p style={{
          fontSize: '0.875rem',
          color: currentVariant.descriptionColor,
          marginBottom: '24px',
          lineHeight: '1.5'
        }}>
          {description}
        </p>
        
        {actionText && onAction && (
          <button 
            style={{
              background: '#ff5a1f',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={onAction}
            onMouseEnter={(e) => {
              e.target.style.background = 'var(--loud-orange-hot, #ff7a45)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#ff5a1f';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {actionText}
          </button>
        )}
      </div>
    </div>
  );
};

// Pre-configured empty states for common scenarios
export const EmptyStates = {
  noProjects: {
    icon: '📁',
    title: 'No Projects Yet',
    description: 'Create your first project to start generating amazing audio content.',
    actionText: 'Create Project'
  },
  
  noAssets: {
    icon: '🎨',
    title: 'No Assets Found',
    description: 'Upload some assets to use in your audio and video projects.',
    actionText: 'Upload Assets'
  },
  
  noAudio: {
    icon: '🎵',
    title: 'No Audio Generated',
    description: 'Start creating audio content with our AI-powered tools.',
    actionText: 'Create Audio'
  },
  
  noVideos: {
    icon: '🎬',
    title: 'No Videos Created',
    description: 'Generate engaging videos by combining your audio and image assets.',
    actionText: 'Create Video'
  },
  
  searchEmpty: {
    icon: '🔍',
    title: 'No Results Found',
    description: 'Try adjusting your search terms or filters.',
    actionText: 'Clear Filters'
  },
  
  error: {
    icon: '⚠️',
    title: 'Something Went Wrong',
    description: 'We couldn\'t load the content. Please try again.',
    actionText: 'Retry',
    variant: 'error'
  }
};

export default EmptyState;
